<template>
    <ObjectiveSectionLayout>
        <CompassIcon slot="icon" size="large" icon="obj-nb-pink-2" />
        <template slot="title">
            {{ $l10n.__('Choisissez la durée de votre objectif') }}
        </template>

        <CompassFormItem
            :label="$t.__('objectives.create.section.ObjectiveSectionDuration.duration')"
            class="objective-duration__select"
        >
            <CompassSelect v-model="durationDate">
                <CompassDropdownSelectOption
                    v-for="duration in settings"
                    :key="duration.value"
                    :label="getDurationLabel(duration.value)"
                    :value="duration.value"
                />
            </CompassSelect>
        </CompassFormItem>

        <div class="objective-duration__picker">
            <!-- MONTH AND YEAR -->
            <template v-if="'quarter' !== durationDate">
                <CompassFormItem
                    :label="
                        $t.__('objectives.create.section.ObjectiveSectionDuration.start_period')
                    "
                    :error="errors && errors.get('period.date_start')"
                >
                    <div :class="{ 'select-error': errors.has('period.date_start') }">
                        <el-date-picker
                            v-model="dateStart"
                            valueFormat="yyyy-MM-dd"
                            :type="typeDate"
                            :format="dateFormatDisplay"
                            :placeholder="
                                $t.__(
                                    'objectives.create.section.ObjectiveSectionDuration.select_date',
                                )
                            "
                            :editable="false"
                            :clearable="false"
                        >
                        </el-date-picker>
                    </div>
                </CompassFormItem>

                <CompassFormItem
                    :label="$t.__('objectives.create.section.ObjectiveSectionDuration.end_period')"
                >
                    <el-date-picker
                        v-model="value['date_end']"
                        :placeholder="
                            $t.__(
                                'objectives.create.section.ObjectiveSectionDuration.select_start_date',
                            )
                        "
                        valueFormat="yyyy-MM-dd"
                        :format="dateFormatDisplay"
                        disabled
                    >
                    </el-date-picker>
                </CompassFormItem>
            </template>

            <!-- QUARTER -->
            <template v-else>
                <CompassFormItem
                    :label="$t.__('objectives.create.section.ObjectiveSectionDuration.select_year')"
                >
                    <el-date-picker
                        v-model="selectedQuarterYear"
                        type="year"
                        :placeholder="
                            $t.__(
                                'objectives.create.section.ObjectiveSectionDuration.select_a_year',
                            )
                        "
                        valueFormat="yyyy"
                        format="yyyy"
                        :editable="false"
                        :clearable="false"
                        @change="(v) => setDateStartFromQuarter(v, selectedQuarterPeriod)"
                    >
                    </el-date-picker>
                </CompassFormItem>

                <CompassFormItem
                    :label="
                        $t.__('objectives.create.section.ObjectiveSectionDuration.select_quarter')
                    "
                >
                    <CompassSelect
                        v-model="selectedQuarterPeriod"
                        size="medium"
                        prefixIcon="calendar"
                        menuClass="quarter-select-menu"
                        @change="(newSelectedQuarter) => updateQuarterPeriod(newSelectedQuarter)"
                    >
                        <CompassDropdownSelectOption
                            v-for="quarterDetail in quarterDetails"
                            :key="`quarter-option-${quarterDetail.code}`"
                            :label="quarterDetail.label"
                            :value="quarterDetail.code"
                            class="quarter-select-option"
                        >
                            <div class="quarter-select-option__label">
                                {{ quarterDetail.label }}
                            </div>

                            <div
                                class="quarter-select-option__detail typographies-paragraph-small-regular"
                            >
                                {{ quarterDetail.details }}
                            </div>
                        </CompassDropdownSelectOption>
                    </CompassSelect>
                </CompassFormItem>
            </template>
        </div>

        <div></div>
    </ObjectiveSectionLayout>
</template>

<script>
import ObjectiveSectionLayout from './ObjectiveSectionLayout.vue';
import ObjectiveSectionMixin from './ObjectiveSectionMixin';

import { createNamespacedHelpers } from 'vuex';
const corpMapState = createNamespacedHelpers('corp').mapState;
export default {
    components: { ObjectiveSectionLayout },
    mixins: [ObjectiveSectionMixin],
    data() {
        return {
            selectedQuarterYear: '',
            selectedQuarterPeriod: '',
            quarterDetails: [
                {
                    code: 1,
                    label: this.$l10n.__('1er trimestre'),
                    details: this.$l10n.__('Janvier - Février - Mars'),
                },
                {
                    code: 2,
                    label: this.$l10n.__('2e trimestre'),
                    details: this.$l10n.__('Avril - Mai - Juin'),
                },
                {
                    code: 3,
                    label: this.$l10n.__('3e trimestre'),
                    details: this.$l10n.__('Juillet - Août - Septembre'),
                },
                {
                    code: 4,
                    label: this.$l10n.__('4e trimestre'),
                    details: this.$l10n.__('Octobre - Novembre - Décembre'),
                },
            ],
        };
    },
    computed: {
        ...corpMapState(['timeformat']),
        dateFormatDisplay() {
            return this.timeformat === 'dmy' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
        },
        typeDate() {
            return this.value.duration === 'month' ? 'month' : 'year';
        },
        /**
         * Set Duration of date. In case of dateStart, adapt it with new duration
         */
        durationDate: {
            get() {
                return this.value.duration;
            },
            set(duration) {
                this.errors.clear('period.date_start');

                switch (duration) {
                    case 'month':
                        this.value['date_start'] = moment().startOf('month').format();
                        break;
                    case 'quarter':
                        this.selectedQuarterYear = moment().year().toString();
                        this.selectedQuarterPeriod = moment().quarter();
                        this.value['date_start'] = moment().startOf('quarter').format();
                        this.$trigger(
                            'objectiveSectionResult:updateQuarterDuration',
                            this.selectedQuarterPeriod,
                        );
                        break;
                    case 'year':
                        this.value['date_start'] = moment().startOf('year').format();
                        break;
                }
                this.$emit('input', {
                    ...this.value,
                    duration,
                    date_end: this.getEndDate(this.dateStart, duration),
                });
                this.$emit('update-rules', {
                    rule: 'period.date_start',
                });
            },
        },
        /**
         * Set Start Date with dynamic End Date by Duration
         */
        dateStart: {
            get() {
                return this.value['date_start'];
            },
            set(dateStart) {
                const newDateStart = moment(dateStart).format();
                if (newDateStart !== this.value['date_start']) {
                    this.$emit('input', {
                        ...this.value,
                        date_start: newDateStart,
                        date_end: this.getEndDate(dateStart, this.durationDate),
                    });
                }
            },
        },
    },
    created() {
        if (this.value.duration === 'quarter') {
            this.selectedQuarterYear = moment(this.value.date_start).year().toString();
            this.selectedQuarterPeriod = moment(this.value.date_start).quarter();
        }
    },
    methods: {
        getEndDate(dateStart, duration) {
            if (!dateStart) return '';

            switch (duration) {
                case 'month':
                    return moment(dateStart).endOf('month').format();
                case 'quarter':
                    return moment(dateStart).endOf('quarter').format();
                case 'year':
                    return moment(dateStart).endOf('year').format();
            }
        },
        setDateStartFromQuarter(selectedYear, selectedQuarter) {
            this.dateStart = moment()
                .year(selectedYear)
                .quarter(selectedQuarter)
                .startOf('quarter');
        },
        updateQuarterPeriod(newSelectedQuarter) {
            this.setDateStartFromQuarter(this.selectedQuarterYear, newSelectedQuarter);
            this.$trigger('objectiveSectionResult:updateQuarterDuration', newSelectedQuarter);
        },
        getDurationLabel(durationValue) {
            let durationLabel = '';
            switch (durationValue) {
                case 'year':
                    durationLabel = this.$t.__('common.labels.a_year');
                    break;
                case 'quarter':
                    durationLabel = this.$t.__('common.labels.a_quarter');
                    break;
                case 'month':
                    durationLabel = this.$t.__('common.labels.a_month');
                    break;
            }
            return durationLabel;
        },
    },
};
</script>

<style lang="scss" scoped>
.objective-duration {
    &__select {
        margin-bottom: var(--spacing-3);
    }

    &__picker {
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-6);

        .compass-form-item,
        .el-date-editor {
            width: 100%;
        }
    }
}
</style>
<style lang="scss">
.quarter-select-menu {
    padding: var(--spacing-3) !important;
    .quarter-select-option {
        .compass-dropdown-option__label {
            display: flex;
            align-items: center;
            flex-direction: column;
            gap: var(--spacing-1);
            width: 100%;
            padding: 0;
        }

        .compass-icon {
            display: none;
        }

        /* STATES */
        &.compass-dropdown-option--selected,
        &.compass-dropdown-option--selected:hover {
            color: var(--colors-common-text-standard-on-color);
            background-color: var(--colors-component-background-actionable-active-default);

            .quarter-select-option__detail {
                color: var(--colors-common-text-contrast-caption);
            }
        }

        /* CHILDREN */
        &__detail {
            color: var(--colors-common-text-standard-caption, #8c909c);
        }
    }
}
</style>
